import React, {memo, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory} from 'react-router-dom';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {useRPC} from '../../../../hooks/rpc';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';
import {useSelector} from 'react-redux';
import {useLocalization} from '../../../../hooks/localization';
import {GatewayInfoModel} from '../../../../types/control';
import {BridgeDeviceModel} from '../../../../types/bridge-device';

const BACK_URL = '/settings';

export const ServerSettingsScreen = memo(function ServerSettingsScreen() {
    const {t} = useLocalization();
    const history = useHistory();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [gatewayList, setGatewayList] = useState<GatewayInfoModel[]>(null);
    const [selectedGateway, setSelectedGateway] = useState<SelectItem<GatewayInfoModel>>(null);

    const gatewaySelectOptionList = useMemo<SelectItem<GatewayInfoModel>[]>(() => {
        let output;
        if (gatewayList == null) {
            output = [];
        } else {
            output = gatewayList.map(gateway => ({
                value: gateway,
                label: gateway.name,
            }));
        }

        output.push({
            value: {id: '', name: t('create_new_gateway')},
            label: t('create_new_gateway'),
        });

        return output;
    }, [gatewayList, t]);

    const connectionAddress = useSelector(state => state.settings.panel.supervisor_connection_address);
    const MACHINE_OPTIONS: SelectItem<boolean>[] = useMemo(() => [
        {
            label: t('this_machine'),
            value: true,
        },
        {
            label: t('other_machine'),
            value: false,
        },
    ], [t]);
    const connectedItem = useMemo(() => {
        if (/localhost/.test(connectionAddress) || /127\.0\.0\.1/.test(connectionAddress)) {
            return MACHINE_OPTIONS.find(a => a.value).label;
        } else {
            return connectionAddress;
        }
    }, [MACHINE_OPTIONS, connectionAddress]);

    const [connectToLocalhost, setConnectToLocalhost] = useState<SelectItem<boolean>>(null);
    const [address, setAddress] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (connectToLocalhost == null) {
            failure(t('pls_select_device_to_connect'));

            return false;
        }

        if (!connectToLocalhost.value) {
            if (gatewayList != null && selectedGateway == null) {
                failure(t('pls_select_a_valid_gateway'));
                return false;
            }

            if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(address) !== true) {
                failure(t('pls_enter_a_valid_ip_address'));

                return false;
            }

            if (username == null || username.length === 0) {
                failure(t('pls_enter_username'));

                return false;
            }

            if (password == null || password.length === 0) {
                failure(t('pls_enter_password'));

                return false;
            }
        }

        return true;
    }, [t, gatewayList, selectedGateway, address, failure, username, password, connectToLocalhost]);

    const onConnectClick = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        if (!connectToLocalhost.value) {
            if (gatewayList == null) {
                rpc.beforeConnectToMachine(address, username, password).then(data => setGatewayList(data.data));
                return;
            }
        }

        (connectToLocalhost.value ? rpc.connectToLocalMachine() : rpc.connectToMachine(address, username, password, selectedGateway.value.id))
            .then(() => {
                success(t('operation_is_in_progress'));
            })
            .catch(failure);
    }, [t, gatewayList, isFieldsValid, connectToLocalhost?.value, success, rpc, address, username, password, failure, selectedGateway]);

    const buttons = useMemo(() => [
        {
            text: t('cancel'),
            className: 'red',
            onClick: onBackPressed,
        },
        {
            text: t('connect'),
            onClick: onConnectClick,
            className: 'green',
        },
    ], [t, onConnectClick, onBackPressed]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    return (
        <PasswordContainer
            requiredRole="service"
            isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingReadOnlyRowComponent
                            title={t('connected_machine')}
                            value={connectedItem}/>
                        <SettingSelectComponent
                            label={t('machine_to_connect')}
                            options={MACHINE_OPTIONS}
                            onChange={setConnectToLocalhost}
                            value={connectToLocalhost}/>
                        {
                            renderIf(connectToLocalhost?.value === false)(() =>
                                <>
                                    <SettingInputComponent
                                        placeholder={t('write_address')}
                                        label={t('address')}
                                        value={address}
                                        setValue={setAddress}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('write_username')}
                                        label={t('username')}
                                        value={username}
                                        setValue={setUsername}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        type="password"
                                        placeholder={t('write_password')}
                                        label={t('password')}
                                        value={password}
                                        setValue={setPassword}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    {
                                        renderIf(gatewayList != null)(() => (
                                            <SettingSelectComponent
                                                label={t('gateway')}
                                                options={gatewaySelectOptionList}
                                                onChange={setSelectedGateway}
                                                value={selectedGateway}/>
                                        ))
                                    }
                                </>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">

                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
